// The player's bottom control bar — a pure function of {state, actions}. Drives both the live
// player (LIVE pill + jump-to-edge, danmaku composer) and VOD (scrub bar + playback rate). It
// auto-hides via the `visible` prop owned by the parent; `onKeepAlive` pins it open while a menu
// or the danmaku input is engaged.
import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Gauge,
  Maximize,
  MessageSquareOff,
  MessageSquareText,
  Minimize,
  Pause,
  PictureInPicture2,
  Play,
  Radio,
  SendHorizontal,
  Settings2,
  Volume1,
  Volume2,
  VolumeX,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover.js";
import type { DanmakuSettings, DanmakuArea, DanmakuSpeed } from "./danmaku/settings.js";
import type { VideoActions, VideoState } from "./useVideoState.js";

const RATES = [0.5, 0.75, 1, 1.25, 1.5, 2];

function fmt(t: number): string {
  if (!Number.isFinite(t) || t < 0) t = 0;
  const s = Math.floor(t % 60);
  const m = Math.floor((t / 60) % 60);
  const h = Math.floor(t / 3600);
  const mm = `${m}`.padStart(h ? 2 : 1, "0");
  const ss = `${s}`.padStart(2, "0");
  return h ? `${h}:${mm}:${ss}` : `${mm}:${ss}`;
}

export interface DanmakuControls {
  settings: DanmakuSettings;
  update: (patch: Partial<DanmakuSettings>) => void;
  canChat: boolean;
  onSend: (text: string) => void;
  onRequireLogin: () => void;
}

export interface ControlsProps {
  mode: "live" | "vod";
  state: VideoState;
  actions: VideoActions;
  visible: boolean;
  onKeepAlive: (keep: boolean) => void;
  lowLatency?: { value: boolean; toggle: () => void };
  tierLabel?: string | null;
  danmaku?: DanmakuControls;
}

const ICON_BTN =
  "inline-flex h-9 w-9 items-center justify-center rounded-md text-white/90 outline-none transition hover:bg-white/15 focus-visible:ring-2 focus-visible:ring-ring";

export function Controls({
  mode,
  state,
  actions,
  visible,
  onKeepAlive,
  lowLatency,
  tierLabel,
  danmaku,
}: ControlsProps) {
  const { t } = useTranslation();
  const VolIcon = state.muted || state.volume === 0 ? VolumeX : state.volume < 0.5 ? Volume1 : Volume2;
  const seekMax = state.duration > 0 ? state.duration : 0;

  return (
    <div
      onClick={(e) => e.stopPropagation()}
      onDoubleClick={(e) => e.stopPropagation()}
      className={cn(
        "absolute inset-x-0 bottom-0 z-20 flex flex-col gap-1.5 bg-gradient-to-t from-black/80 via-black/45 to-transparent px-2 pb-2 pt-8 transition-[opacity,transform] duration-200",
        visible ? "translate-y-0 opacity-100" : "pointer-events-none translate-y-2 opacity-0",
      )}
    >
      {/* VOD scrubber */}
      {mode === "vod" && (
        <div className="flex items-center gap-2 px-1">
          <span className="w-12 shrink-0 text-right font-mono text-[11px] tabular-nums text-white/80">
            {fmt(state.currentTime)}
          </span>
          <input
            type="range"
            min={0}
            max={seekMax || 1}
            step="any"
            value={Math.min(state.currentTime, seekMax || 0)}
            onChange={(e) => actions.seek(Number(e.target.value))}
            aria-label={t("player.seek")}
            className="h-1.5 flex-1 cursor-pointer accent-primary"
          />
          <span className="w-12 shrink-0 font-mono text-[11px] tabular-nums text-white/60">
            {fmt(state.duration)}
          </span>
        </div>
      )}

      {/* Transport row */}
      <div className="flex items-center gap-1">
        <button onClick={actions.togglePlay} className={ICON_BTN} aria-label={t("player.playPause")}>
          {state.playing ? <Pause className="h-5 w-5" /> : <Play className="h-5 w-5" />}
        </button>

        {/* Volume */}
        <div className="group/vol flex items-center">
          <button onClick={actions.toggleMute} className={ICON_BTN} aria-label={t("player.mute")}>
            <VolIcon className="h-5 w-5" />
          </button>
          <input
            type="range"
            min={0}
            max={1}
            step={0.01}
            value={state.muted ? 0 : state.volume}
            onChange={(e) => actions.setVolume(Number(e.target.value))}
            aria-label={t("player.volume")}
            className="ml-0.5 hidden h-1.5 w-0 cursor-pointer accent-primary opacity-0 transition-all duration-200 group-hover/vol:w-16 group-hover/vol:opacity-100 focus:w-16 focus:opacity-100 sm:block"
          />
        </div>

        {/* Live pill or VOD time */}
        {mode === "live" ? (
          <button
            onClick={actions.toLiveEdge}
            disabled={state.behindLive < 1}
            title={state.behindLive >= 1 ? t("player.goLive") : undefined}
            className={cn(
              "ml-1 inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold uppercase tracking-wide outline-none transition focus-visible:ring-2 focus-visible:ring-ring",
              state.behindLive < 1
                ? "text-white/90"
                : "cursor-pointer bg-white/10 text-white/55 hover:bg-white/15",
            )}
          >
            <span
              className={cn(
                "h-2 w-2 rounded-full",
                state.behindLive < 1 ? "animate-livepulse bg-destructive" : "bg-white/50",
              )}
            />
            {t("status.live")}
          </button>
        ) : (
          <span className="ml-1 hidden font-mono text-[11px] tabular-nums text-white/70 sm:inline">
            {fmt(state.currentTime)} / {fmt(state.duration)}
          </span>
        )}

        {/* Danmaku composer (live, sm+) grows to fill; a spacer keeps the clusters apart elsewhere */}
        {danmaku && mode === "live" && <DanmakuComposer danmaku={danmaku} onKeepAlive={onKeepAlive} />}
        <div className={cn("flex-1", danmaku && mode === "live" && "sm:hidden")} />

        {/* Right cluster */}
        {tierLabel && (
          <span className="mr-1 hidden rounded bg-white/10 px-1.5 py-0.5 font-mono text-[10px] text-white/70 md:inline">
            {tierLabel}
          </span>
        )}

        {mode === "vod" && (
          <RatePopover rate={state.playbackRate} onPick={actions.setPlaybackRate} onKeepAlive={onKeepAlive} />
        )}

        {danmaku && (
          <>
            <button
              onClick={() => danmaku.update({ enabled: !danmaku.settings.enabled })}
              className={cn(ICON_BTN, danmaku.settings.enabled ? "text-primary" : "text-white/55")}
              title={danmaku.settings.enabled ? t("danmaku.on") : t("danmaku.off")}
            >
              {danmaku.settings.enabled ? (
                <MessageSquareText className="h-5 w-5" />
              ) : (
                <MessageSquareOff className="h-5 w-5" />
              )}
            </button>
            <DanmakuSettingsPopover danmaku={danmaku} onKeepAlive={onKeepAlive} />
          </>
        )}

        {lowLatency && (
          <button
            onClick={lowLatency.toggle}
            className={cn(ICON_BTN, lowLatency.value ? "text-primary" : "text-white/55")}
            title={lowLatency.value ? t("player.llOn") : t("player.llOff")}
          >
            <Gauge className="h-5 w-5" />
          </button>
        )}

        {state.pipSupported && (
          <button onClick={actions.togglePip} className={ICON_BTN} title={t("player.pip")}>
            <PictureInPicture2 className="h-5 w-5" />
          </button>
        )}

        <button onClick={actions.toggleFullscreen} className={ICON_BTN} title={t("player.fullscreen")}>
          {state.isFullscreen ? <Minimize className="h-5 w-5" /> : <Maximize className="h-5 w-5" />}
        </button>
      </div>
    </div>
  );
}

function DanmakuComposer({
  danmaku,
  onKeepAlive,
}: {
  danmaku: DanmakuControls;
  onKeepAlive: (keep: boolean) => void;
}) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState("");
  if (!danmaku.canChat) {
    return (
      <button
        onClick={danmaku.onRequireLogin}
        className="mx-2 hidden flex-1 rounded-full border border-white/15 bg-white/5 px-3 py-1.5 text-left text-xs text-white/55 outline-none transition hover:bg-white/10 focus-visible:ring-2 focus-visible:ring-ring sm:block"
      >
        {t("danmaku.loginToSend")}
      </button>
    );
  }
  const send = () => {
    const body = draft.trim();
    if (!body) return;
    danmaku.onSend(body);
    setDraft("");
  };
  return (
    <div className="mx-2 hidden flex-1 items-center gap-1 rounded-full border border-white/15 bg-black/40 pl-3 pr-1 focus-within:border-primary/60 sm:flex">
      <MessageSquareText className="h-3.5 w-3.5 shrink-0 text-white/45" />
      <input
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onFocus={() => onKeepAlive(true)}
        onBlur={() => onKeepAlive(false)}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            send();
          }
          e.stopPropagation(); // don't trigger player hotkeys while typing
        }}
        maxLength={200}
        placeholder={t("danmaku.placeholder")}
        className="min-w-0 flex-1 bg-transparent py-1.5 text-xs text-white outline-none placeholder:text-white/40"
      />
      <button
        onClick={send}
        disabled={!draft.trim()}
        aria-label={t("danmaku.send")}
        className="inline-flex h-7 w-7 items-center justify-center rounded-full text-primary outline-none transition hover:bg-white/10 disabled:opacity-40 focus-visible:ring-2 focus-visible:ring-ring"
      >
        <SendHorizontal className="h-4 w-4" />
      </button>
    </div>
  );
}

function Segmented<T extends string | number>({
  value,
  options,
  onChange,
}: {
  value: T;
  options: { value: T; label: string }[];
  onChange: (v: T) => void;
}) {
  return (
    <div className="flex gap-1 rounded-lg bg-white/5 p-1">
      {options.map((o) => (
        <button
          key={String(o.value)}
          onClick={() => onChange(o.value)}
          className={cn(
            "flex-1 rounded-md px-2 py-1 text-xs font-medium outline-none transition focus-visible:ring-2 focus-visible:ring-ring",
            value === o.value ? "bg-primary text-primary-foreground" : "text-white/70 hover:bg-white/10",
          )}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

function DanmakuSettingsPopover({
  danmaku,
  onKeepAlive,
}: {
  danmaku: DanmakuControls;
  onKeepAlive: (keep: boolean) => void;
}) {
  const { t } = useTranslation();
  const { settings, update } = danmaku;
  return (
    <Popover onOpenChange={onKeepAlive}>
      <PopoverTrigger asChild>
        <button className={ICON_BTN} title={t("danmaku.settings")}>
          <Settings2 className="h-5 w-5" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-64 space-y-3.5">
        <div className="space-y-1.5">
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <span>{t("danmaku.opacity")}</span>
            <span className="font-mono tabular-nums">{Math.round(settings.opacity * 100)}%</span>
          </div>
          <input
            type="range"
            min={0.2}
            max={1}
            step={0.05}
            value={settings.opacity}
            onChange={(e) => update({ opacity: Number(e.target.value) })}
            className="h-1.5 w-full cursor-pointer accent-primary"
          />
        </div>
        <div className="space-y-1.5">
          <div className="text-xs text-muted-foreground">{t("danmaku.area")}</div>
          <Segmented<DanmakuArea>
            value={settings.area}
            onChange={(v) => update({ area: v })}
            options={[
              { value: "quarter", label: t("danmaku.areaQuarter") },
              { value: "half", label: t("danmaku.areaHalf") },
              { value: "full", label: t("danmaku.areaFull") },
            ]}
          />
        </div>
        <div className="space-y-1.5">
          <div className="text-xs text-muted-foreground">{t("danmaku.speed")}</div>
          <Segmented<DanmakuSpeed>
            value={settings.speed}
            onChange={(v) => update({ speed: v })}
            options={[
              { value: "slow", label: t("danmaku.speedSlow") },
              { value: "normal", label: t("danmaku.speedNormal") },
              { value: "fast", label: t("danmaku.speedFast") },
            ]}
          />
        </div>
        <div className="space-y-1.5">
          <div className="text-xs text-muted-foreground">{t("danmaku.fontSize")}</div>
          <Segmented<number>
            value={settings.fontScale as number}
            onChange={(v) => update({ fontScale: v })}
            options={[
              { value: 0.8, label: t("danmaku.fontSmall") },
              { value: 1, label: t("danmaku.fontNormal") },
              { value: 1.25, label: t("danmaku.fontLarge") },
            ]}
          />
        </div>
      </PopoverContent>
    </Popover>
  );
}

function RatePopover({
  rate,
  onPick,
  onKeepAlive,
}: {
  rate: number;
  onPick: (r: number) => void;
  onKeepAlive: (keep: boolean) => void;
}) {
  return (
    <Popover onOpenChange={onKeepAlive}>
      <PopoverTrigger asChild>
        <button
          className="inline-flex h-9 min-w-9 items-center justify-center rounded-md px-2 font-mono text-xs text-white/90 outline-none transition hover:bg-white/15 focus-visible:ring-2 focus-visible:ring-ring"
          title="Speed"
        >
          {rate}×
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-28 p-1">
        {RATES.map((r) => (
          <button
            key={r}
            onClick={() => onPick(r)}
            className={cn(
              "flex w-full items-center justify-between rounded-md px-2.5 py-1.5 text-sm outline-none transition hover:bg-white/10 focus-visible:bg-white/10",
              r === rate ? "text-primary" : "text-foreground/80",
            )}
          >
            {r}×
          </button>
        ))}
      </PopoverContent>
    </Popover>
  );
}
